/* eslint-disable no-nested-ternary */
import React, { useMemo, useContext, forwardRef } from 'react';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Animation from '../../../components/Animation';
import LazyLoad from '../../../components/LazyLoad';
import CenteredLoader from '../../../components/CenteredLoader';
import { Context } from '../../../context/AppContext';
import getPackage from '../../../utils/getPackage';
import useScreenOrientation from '../../../hooks/useScreenOrientation';
import listviewLoader from '../../../animations/listviewLoader.json';
import CardWrapper from '../CardWrapper';
import FeatureReviews from '../FeatureReviews';
import Card from './Card';
import NoResults from '../NoResults';
import './styles.css';

const SponsoredBusinesses = loadable(
  () => import('../../../components/SponsoredBusinesses'),
  { fallback: <CenteredLoader /> }
);

const FeaturedPhotos = loadable(() => import('../FeaturedPhotos'), {
  fallback: <CenteredLoader />,
});

const MissingPlace = loadable(() => import('../MissingPlace'), {
  fallback: <CenteredLoader />,
});

const Cards = forwardRef(
  (
    {
      data,
      loading,
      market,
      banners,
      searchKeyword,
      featuredPhotos,
      suburb,
      categoryId,
      featureReviews,
      regionCitySearch,
    },
    ref
  ) => {
    const {
      media: { desktop, mobile, tablet },
    } = useContext(Context);
    const orientation = useScreenOrientation();

    const insertIndex = useMemo(() => {
      const init = {
        featureReviews: -1,
        banners: -1,
        featuredPhotos: -1,
      };
      if (data && !!data.length) {
        const lastIndex = () => data.length - 1;
        const getHalfIndex = () => Math.round(Math.max(data.length / 2)) - 1;
        const getLastPaidIndex = () =>
          data
            .filter(({ attributes: { paid } }) => paid)
            .reduce((agg, item, index) => {
              return index;
            }, 0);
        const getLastGoldIndex = () =>
          data.reduce(
            (
              agg,
              {
                attributes: {
                  paid,
                  package: packageType,
                  category_score: categoryScore,
                },
              },
              index
            ) => {
              const packageName = getPackage({
                categoryScore,
                packageType,
                paid,

                category: searchKeyword || '',
              });
              if (paid && packageName === 'Gold') {
                return index;
              }
              return agg;
            },
            0
          );

        let initIndex;
        if (featureReviews && featureReviews.length) {
          if (getLastGoldIndex()) {
            initIndex = getLastGoldIndex();
            init.featureReviews = initIndex;
          } else if (getHalfIndex()) {
            initIndex = getHalfIndex();
            init.featureReviews = initIndex;
          } else {
            initIndex = lastIndex();
            init.featureReviews = initIndex;
          }
        }
        if (featuredPhotos && featuredPhotos.length) {
          if (initIndex) {
            initIndex += 4;
            init.featuredPhotos =
              initIndex > lastIndex() ? lastIndex() : initIndex;
          } else if (getHalfIndex()) {
            initIndex = getHalfIndex();
            init.featuredPhotos = initIndex;
          } else {
            initIndex = lastIndex();
            init.featuredPhotos = initIndex;
          }
        }
        if (banners && !!banners.length) {
          if (getLastPaidIndex()) {
            initIndex = getLastPaidIndex();
            init.banners = initIndex;
          } else if (getHalfIndex()) {
            initIndex = getHalfIndex();
            init.banners = initIndex;
          } else {
            initIndex = lastIndex();
            init.banners = initIndex;
          }
        }
      }

      return init;
    }, [data, featureReviews, searchKeyword, banners, featuredPhotos]);

    return (
      <>
        <div className="Listview__Cards__root" ref={ref}>
          {loading ? (
            <Animation animation={listviewLoader} width={300} />
          ) : data && !!data.length ? (
            <>
              {data.map(({ attributes }, key) => (
                <React.Fragment key={attributes.listing_id}>
                  <CardWrapper className="Listview__Cards__item">
                    <Card
                      searchKeyword={searchKeyword}
                      data={attributes}
                      market={market}
                      index={key}
                      categoryId={categoryId}
                      regionCitySearch={regionCitySearch}
                    />
                  </CardWrapper>
                  {insertIndex.featureReviews > -1 &&
                    insertIndex.featureReviews === key && (
                      <div className="Listview__Cards__seoBanners">
                        <FeatureReviews
                          reviews={featureReviews}
                          title={`Featured Reviews of the Best ${searchKeyword} in ${suburb}`}
                        />
                      </div>
                    )}
                  {insertIndex.banners > -1 &&
                    insertIndex.banners === key &&
                    banners &&
                    !!banners.length &&
                    (insertIndex.banners !== data.length - 1 ||
                      (desktop || tablet)) && (
                      <div className="Listview__Cards__seoBanners">
                        <LazyLoad>
                          <div className="Listview__Cards__seoBanners__inner">
                            {desktop || tablet ? (
                              <>
                                <div className="Listview__Cards__h2">
                                  <h2>Best {searchKeyword} near you</h2>
                                </div>
                                <SponsoredBusinesses
                                  className={classNames(
                                    'Listview__Cards__SponsoredBusinesses',
                                    {
                                      [`Listview__Cards__SponsoredBusinesses--${banners &&
                                        banners.length}`]:
                                        banners && banners.length,
                                    }
                                  )}
                                  data={banners}
                                  contactUs={banners.length === 1 && desktop}
                                  visibleSlides={
                                    orientation === 'portrait-primary' || tablet
                                      ? 1
                                      : 2
                                  }
                                  split={1}
                                  target="_blank"
                                  interval={8000}
                                  isPlaying={banners && banners.length > 1}
                                  arrows={banners && banners.length > 1}
                                />
                              </>
                            ) : (
                              insertIndex.banners !== data.length - 1 && (
                                <>
                                  <div className="Listview__Cards__h2">
                                    <h2>Best {searchKeyword} near you</h2>
                                  </div>
                                  <SponsoredBusinesses
                                    className={classNames(
                                      'Listview__Cards__SponsoredBusinesses',
                                      {
                                        [`Listview__Cards__SponsoredBusinesses--${banners &&
                                          banners.length}`]:
                                          banners && banners.length,
                                      }
                                    )}
                                    data={banners}
                                    visibleSlides={1}
                                    split={1}
                                    interval={8000}
                                    isPlaying={banners && banners.length > 1}
                                    arrows={banners && banners.length > 1}
                                  />
                                </>
                              )
                            )}
                          </div>
                        </LazyLoad>
                      </div>
                    )}
                  {insertIndex.featuredPhotos > -1 &&
                    insertIndex.featuredPhotos === key &&
                    featuredPhotos &&
                    !!featuredPhotos.length && (
                      <div className="Listview__Cards__seoBanners">
                        <div className="Listview__Cards__seoBanners__inner">
                          <LazyLoad>
                            <FeaturedPhotos
                              suburb={suburb}
                              category={searchKeyword}
                              photos={featuredPhotos}
                            />
                          </LazyLoad>
                        </div>
                      </div>
                    )}
                </React.Fragment>
              ))}
              {mobile &&
                insertIndex.banners === (data || []).length - 1 &&
                banners &&
                !!banners.length && (
                  <div className="Listview__Cards__seoBanners">
                    <div className="Listview__Cards__h2">
                      <h2>Best {searchKeyword} near you</h2>
                    </div>
                    <LazyLoad>
                      <div className="Listview__Cards__seoBanners__inner">
                        <SponsoredBusinesses
                          className={classNames(
                            'Listview__Cards__SponsoredBusinesses',
                            {
                              [`Listview__Cards__SponsoredBusinesses--${banners &&
                                banners.length}`]: banners && banners.length,
                            }
                          )}
                          data={banners}
                          visibleSlides={1}
                          split={1}
                          interval={8000}
                          isPlaying={banners && banners.length > 1}
                          arrows={banners && banners.length > 1}
                        />
                      </div>
                    </LazyLoad>
                  </div>
                )}
            </>
          ) : (
            !loading && <NoResults />
          )}
        </div>
        <MissingPlace />
      </>
    );
  }
);

Cards.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
};

Cards.defaultProps = {
  data: [],
};

export default Cards;
